import React, { useState, useEffect } from 'react';
import { adminApi } from '../../../services/api';
import { Target, Users, Gift, TrendingUp, BarChart3 } from 'lucide-react';

export function CampaignsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get('/admin/campaigns/analytics/overview')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-16 text-[#64748B]">Caricamento...</div>;
  if (!data || !data.campaigns?.length) return (
    <div className="text-center py-16">
      <Target size={40} className="mx-auto mb-3 text-[#ccc]" />
      <p className="text-[#64748B]">Nessuna campagna attiva</p>
      <p className="text-[#aaa] text-sm mt-1">Crea campagne fisiche per vedere le analytics</p>
    </div>
  );

  const t = data.totals;
  const DOW_LABELS = ['', 'Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

  // Build heatmap grid
  const heatmap = {};
  let maxCount = 1;
  (data.hourly_heatmap || []).forEach(h => {
    const key = `${h.day}-${h.hour}`;
    heatmap[key] = h.count;
    if (h.count > maxCount) maxCount = h.count;
  });

  return (
    <div className="space-y-6" data-testid="campaigns-analytics-tab">
      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Scan Totali" value={t.total_scans} icon={<Target size={18} />} color="#5B8DEF" />
        <KPI label="Registrazioni" value={t.total_assignments} icon={<Users size={18} />} color="#D44E20" />
        <KPI label="Riscattati" value={t.total_redeemed} icon={<Gift size={18} />} color="#3ECF8E" />
        <KPI label="Conversione" value={`${t.overall_conversion}%`} icon={<TrendingUp size={18} />} color="#FFAA00" sub="Scan → Registrazione" />
        <KPI label="Tasso Riscatto" value={`${t.overall_redeem_rate}%`} icon={<BarChart3 size={18} />} color="#A78BFA" sub="Assegnati → Riscattati" />
      </div>

      {/* Per-campaign table */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h3 className="font-bold text-[#111] mb-4">Performance per Campagna</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#64748B] border-b">
              <th className="pb-2 pr-4">Campagna</th>
              <th className="pb-2 text-center">Stato</th>
              <th className="pb-2 text-right">Scan</th>
              <th className="pb-2 text-right">Registrazioni</th>
              <th className="pb-2 text-right">Conversione</th>
              <th className="pb-2 text-right">Riscattati</th>
              <th className="pb-2 text-right">% Riscatto</th>
            </tr></thead>
            <tbody>
              {data.campaigns.map(c => (
                <tr key={c.id} className="border-b border-gray-50">
                  <td className="py-3 pr-4">
                    <div className="font-semibold text-[#111]">{c.name}</div>
                    {c.start_date && <div className="text-[10px] text-[#64748B] mt-0.5">{c.start_date} → {c.end_date || '∞'}</div>}
                  </td>
                  <td className="py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-gray-500'}`}>
                      {c.status === 'active' ? 'Attiva' : c.status}
                    </span>
                  </td>
                  <td className="py-3 text-right font-mono">{c.scans}</td>
                  <td className="py-3 text-right font-mono">{c.assignments}</td>
                  <td className="py-3 text-right">
                    <span className={`font-bold ${c.conversion_rate > 10 ? 'text-emerald-600' : c.conversion_rate > 0 ? 'text-amber-600' : 'text-[#64748B]'}`}>
                      {c.conversion_rate}%
                    </span>
                  </td>
                  <td className="py-3 text-right font-mono">{c.redeemed}</td>
                  <td className="py-3 text-right">
                    <span className={`font-bold ${c.redeem_rate > 50 ? 'text-emerald-600' : c.redeem_rate > 0 ? 'text-amber-600' : 'text-[#64748B]'}`}>
                      {c.redeem_rate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Heatmap */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <h3 className="font-bold text-[#111] mb-4">Heatmap Oraria Scan</h3>
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              <div className="flex items-center gap-0.5 mb-1">
                <div className="w-8" />
                {Array.from({length: 24}, (_, h) => (
                  <div key={h} className="w-6 text-center text-[8px] text-[#64748B]">{h}</div>
                ))}
              </div>
              {[2, 3, 4, 5, 6, 7, 1].map(dow => (
                <div key={dow} className="flex items-center gap-0.5 mb-0.5">
                  <div className="w-8 text-[9px] text-[#64748B] font-medium">{DOW_LABELS[dow]}</div>
                  {Array.from({length: 24}, (_, h) => {
                    const count = heatmap[`${dow}-${h}`] || 0;
                    const intensity = count / maxCount;
                    return (
                      <div key={h} className="w-6 h-5 rounded-sm" title={`${DOW_LABELS[dow]} ${h}:00 — ${count} scan`}
                        style={{ backgroundColor: count === 0 ? '#f3f4f6' : `rgba(212, 78, 32, ${0.15 + intensity * 0.85})` }}
                      />
                    );
                  })}
                </div>
              ))}
              <div className="flex items-center gap-2 mt-3 text-[9px] text-[#64748B]">
                <span>Meno</span>
                {[0, 0.25, 0.5, 0.75, 1].map((v, i) => (
                  <div key={i} className="w-4 h-3 rounded-sm" style={{ backgroundColor: v === 0 ? '#f3f4f6' : `rgba(212, 78, 32, ${0.15 + v * 0.85})` }} />
                ))}
                <span>Più</span>
              </div>
            </div>
          </div>
        </div>

        {/* Prize distribution */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <h3 className="font-bold text-[#111] mb-4">Distribuzione Premi Assegnati</h3>
          {(data.prize_distribution || []).length === 0 ? (
            <p className="text-[#64748B] text-sm text-center py-8">Nessun premio assegnato</p>
          ) : (
            <div className="space-y-3">
              {data.prize_distribution.map((p, i) => {
                const maxP = data.prize_distribution[0]?.count || 1;
                const pct = Math.round(p.count / maxP * 100);
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#333] font-medium">{p.name}</span>
                      <span className="text-[#64748B] font-mono">{p.count}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: ['#D44E20', '#3ECF8E', '#5B8DEF', '#FFAA00', '#A78BFA'][i % 5] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Daily scan trend */}
      {(data.daily_scans || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <h3 className="font-bold text-[#111] mb-4">Trend Scan Giornaliero (30gg)</h3>
          <div className="flex items-end gap-0.5 h-32">
            {(() => {
              const maxD = Math.max(...data.daily_scans.map(d => d.scans), 1);
              return data.daily_scans.map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center group relative">
                  <div className="absolute -top-6 hidden group-hover:block bg-[#111] text-white text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap">
                    {d.date}: {d.scans}
                  </div>
                  <div className="w-full rounded-t" style={{ height: `${Math.max((d.scans / maxD) * 100, 3)}%`, backgroundColor: '#D44E20', opacity: 0.7 + (d.scans / maxD) * 0.3 }} />
                </div>
              ));
            })()}
          </div>
          <div className="flex justify-between text-[8px] text-[#64748B] mt-1">
            <span>{data.daily_scans[0]?.date?.slice(5)}</span>
            <span>{data.daily_scans[data.daily_scans.length - 1]?.date?.slice(5)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, icon, color, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: `${color}15`, color }}>{icon}</div>
      <div className="text-xl font-bold text-[#111]">{value}</div>
      <div className="text-[11px] text-[#64748B]">{label}</div>
      {sub && <div className="text-[9px] text-[#aaa] mt-0.5">{sub}</div>}
    </div>
  );
}
